import { EmailVerificationDomain } from 'models/EmailVerification';
import { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { setExceptionError } from 'stores/errorSlice';
import { useAppDispatch } from 'stores/type';

export const useEmailVerification = (): [
  string | undefined,
  string | undefined,
  string | undefined,
  string | undefined,
  (toEmail: string, sentCallback?: () => void) => Promise<void>,
  (sentCallback: () => void) => Promise<void>,
  (code: string) => Promise<void>
] => {
  const [verifyingEmail, setVerifyingEmail] = useState<string>();
  const [verifiedEmail, setVerifiedEmail] = useState<string>();
  const [transactionKey, setTransactionKey] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const intl = useIntl();
  const dispatch = useAppDispatch();

  const sendVerificationCode = useCallback(
    async (toEmail: string, sentCallback?: () => void) => {
      EmailVerificationDomain.createTransaction(toEmail)
        .then((result) => {
          if (result.isFailure()) {
            setErrorMessage(
              intl.formatMessage({ id: result.value.localeId() })
            );
          }

          if (result.isSuccess()) {
            setErrorMessage(undefined);
            setTransactionKey(result.value);
            setVerifyingEmail(toEmail);
            if (sentCallback) {
              sentCallback();
            }
          }
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    },
    []
  );

  const reSendVerificationCode = useCallback(
    async (sentCallback: () => void) => {
      if (verifyingEmail !== undefined) {
        sendVerificationCode(verifyingEmail, sentCallback);
      }
    },
    [verifyingEmail]
  );

  const verifyEmail = useCallback(
    async (code: string) => {
      if (transactionKey === undefined) {
        return;
      }

      EmailVerificationDomain.validateTransaction(transactionKey, code)
        .then((result) => {
          if (result.isFailure()) {
            setErrorMessage(
              intl.formatMessage({ id: result.value.localeId() })
            );
          }

          if (result.isSuccess()) {
            setErrorMessage(undefined);
            setVerifiedEmail(verifyingEmail);
          }
        })
        .catch((e) => {
          dispatch(setExceptionError(e));
        });
    },
    [transactionKey, verifyingEmail]
  );

  return [
    transactionKey,
    verifyingEmail,
    verifiedEmail,
    errorMessage,
    sendVerificationCode,
    reSendVerificationCode,
    verifyEmail,
  ];
};
